import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import VerifiedBadge from "./VerifiedBadge";
import SourcePlatformBadge from "./SourcePlatformBadge";

export default function StoriesRail() {
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);

  useEffect(() => {
    api.get("/videos/shorts?limit=12").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const updateArrows = () => {
    const el = ref.current;
    if (!el) return;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = ref.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section data-testid="stories-rail" className="px-3 sm:px-6 md:px-10 mt-8 max-w-7xl mx-auto relative">
      <div className="flex items-end justify-between mb-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Tap Stories</div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight mt-1">Vertical, fast, addictive</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex gap-2">
            <button
              data-testid="stories-scroll-left"
              onClick={() => scroll(-1)}
              disabled={!canL}
              aria-label="Scroll stories left"
              className="tv-glass w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="stories-scroll-right"
              onClick={() => scroll(1)}
              disabled={!canR}
              aria-label="Scroll stories right"
              className="tv-glass w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <Link to="/shorts" className="text-xs text-white/60 hover:text-white whitespace-nowrap">Open feed →</Link>
        </div>
      </div>

      <div
        ref={ref}
        className="flex gap-3 overflow-x-auto hide-scrollbar scroll-smooth pb-3 snap-x snap-mandatory"
      >
        {items.map((v) => (
          <Link
            key={v.id}
            to={`/shorts?id=${v.id}`}
            data-testid={`story-tile-${v.id}`}
            className="relative shrink-0 snap-start w-[120px] sm:w-[140px] aspect-[9/16] rounded-xl overflow-hidden border border-white/10 hover:border-tv-accent/50 transition group"
          >
            <img
              src={v.thumbnail_url || "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=360&q=80"}
              alt={v.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            {v.source_platform && (
              <div className="absolute top-1.5 left-1.5">
                <SourcePlatformBadge platform={v.source_platform} label={v.source_platform_label} />
              </div>
            )}
            <div className="absolute bottom-2 left-2 right-2">
              <div className="flex items-center gap-1">
                <img
                  src={v.creator_avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=48&q=80"}
                  alt=""
                  className="w-6 h-6 rounded-full border border-white/40"
                />
                {v.creator_is_top && <VerifiedBadge gold size={12} />}
              </div>
              <div className="text-white text-[10px] font-semibold line-clamp-2 leading-tight mt-1">{v.title}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
